/* ==========================================================================
   ARTEECA WORLD — comportements en arrière-plan
   Aucune dépendance. Configuration lue dans <script id="ar-config">.
   ========================================================================== */
(function () {
  'use strict';

  /* ---------------------------------------------------------------------
     Phase de lune (calcul astronomique simplifié, précision < 1 %)
     Exposé aussi pour les tests Node (module.exports).
     --------------------------------------------------------------------- */
  const RAD = Math.PI / 180;
  const norm = (x) => ((x % 360) + 360) % 360;
  const sin = (deg) => Math.sin(deg * RAD);

  function moonElongation(date) {
    const jd = date.getTime() / 86400000 + 2440587.5;
    const T = (jd - 2451545.0) / 36525;
    const Lp = norm(218.3164477 + 481267.88123421 * T);
    const D = norm(297.8501921 + 445267.1114034 * T);
    const M = norm(357.5291092 + 35999.0502909 * T);
    const Mp = norm(134.9633964 + 477198.8675055 * T);
    const F = norm(93.272095 + 483202.0175233 * T);
    const moonLon =
      Lp +
      6.289 * sin(Mp) +
      1.274 * sin(2 * D - Mp) +
      0.658 * sin(2 * D) +
      0.214 * sin(2 * Mp) -
      0.186 * sin(M) -
      0.114 * sin(2 * F) -
      0.059 * sin(2 * D - 2 * Mp) -
      0.057 * sin(2 * D - M - Mp) +
      0.053 * sin(2 * D + Mp) +
      0.046 * sin(2 * D - M) +
      0.041 * sin(Mp - M) -
      0.035 * sin(D) -
      0.031 * sin(Mp + M);
    const L0 = norm(280.46646 + 36000.76983 * T);
    const sunLon = L0 + 1.914602 * sin(M) + 0.019993 * sin(2 * M);
    return norm(moonLon - sunLon);
  }

  const illumination = (psi) => (1 - Math.cos(psi * RAD)) / 2;

  function phaseKey(psi) {
    const keys = [
      'new',
      'waxing_crescent',
      'first_quarter',
      'waxing_gibbous',
      'full',
      'waning_gibbous',
      'last_quarter',
      'waning_crescent',
    ];
    return keys[Math.floor(((psi + 22.5) % 360) / 45)];
  }

  /* Tracé SVG de la partie éclairée (vue de l'hémisphère nord) */
  function moonPath(psi, cx, cy, r) {
    const k = Math.cos(psi * RAD);
    const rx = Math.abs(k) * r;
    const waxing = psi < 180;
    const limbSweep = waxing ? 1 : 0;
    const termSweep = waxing ? (k > 0 ? 0 : 1) : k > 0 ? 1 : 0;
    const f = (n) => Math.round(n * 100) / 100;
    return (
      'M ' + cx + ' ' + (cy - r) +
      ' A ' + r + ' ' + r + ' 0 0 ' + limbSweep + ' ' + cx + ' ' + (cy + r) +
      ' A ' + f(rx) + ' ' + r + ' 0 0 ' + termSweep + ' ' + cx + ' ' + (cy - r) + ' Z'
    );
  }

  const MoonAPI = { moonElongation, illumination, phaseKey, moonPath };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = MoonAPI;
    return;
  }
  if (typeof document === 'undefined') return;
  window.ArteecaMoon = MoonAPI;

  /* ---------------------------------------------------------------------
     Configuration & utilitaires
     --------------------------------------------------------------------- */
  const doc = document;
  let cfg = {};
  try {
    const el = doc.getElementById('ar-config');
    if (el) cfg = JSON.parse(el.textContent || '{}');
  } catch (e) {
    cfg = {};
  }

  const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const saveData = !!(navigator.connection && navigator.connection.saveData);
  const noop = function () {};

  function ready(fn) {
    if (doc.readyState !== 'loading') fn();
    else doc.addEventListener('DOMContentLoaded', fn);
  }

  function store(kind) {
    try {
      const s = window[kind];
      s.setItem('__ar', '1');
      s.removeItem('__ar');
      return s;
    } catch (e) {
      return null;
    }
  }
  const ls = store('localStorage');
  const ss = store('sessionStorage');

  /* ---------------------------------------------------------------------
     1. Film de marque (hero + section film)
     --------------------------------------------------------------------- */
  function initFilm(root) {
    const video = root.querySelector('video');
    if (!video) return;
    const replay = root.querySelector('[data-ar-replay]');

    if (reduceMotion || saveData) {
      root.classList.add('is-ended');
      return;
    }

    const fail = () => root.classList.add('is-ended');
    const play = () => {
      root.classList.remove('is-ended');
      try {
        video.currentTime = 0;
      } catch (e) {
        noop();
      }
      const p = video.play();
      if (p && p.catch) p.catch(fail);
    };

    video.addEventListener('playing', () => root.classList.add('is-playing'));
    video.addEventListener('ended', () => root.classList.add('is-ended'));
    video.addEventListener('error', fail);
    if (replay) replay.addEventListener('click', play);

    let started = false;
    const start = () => {
      if (started) return;
      started = true;
      const p = video.play();
      if (p && p.catch) p.catch(fail);
    };

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            start();
            io.disconnect();
          }
        },
        { threshold: 0.35 }
      );
      io.observe(root);
    } else {
      start();
    }
  }

  /* ---------------------------------------------------------------------
     2. Attribution marketing → attributs du panier (visibles dans la commande)
     --------------------------------------------------------------------- */
  function initAttribution() {
    if (!cfg.utm || !ss || !window.fetch) return;
    const FLAG = 'ar_attr_sent';
    const params = new URLSearchParams(location.search);
    const hasCampaign = ['utm_source', 'utm_medium', 'utm_campaign', 'fbclid', 'ttclid', 'gclid'].some((k) => params.has(k));
    let ext = false;
    try {
      ext = !!doc.referrer && new URL(doc.referrer).hostname !== location.hostname;
    } catch (e) {
      ext = false;
    }

    const attrs = () => {
      const a = {};
      const src = params.get('utm_source') || (params.get('fbclid') ? 'facebook' : params.get('ttclid') ? 'tiktok' : params.get('gclid') ? 'google' : '');
      if (src) a.utm_source = src;
      if (params.get('utm_medium')) a.utm_medium = params.get('utm_medium');
      if (params.get('utm_campaign')) a.utm_campaign = params.get('utm_campaign');
      if (ext) {
        try {
          a.referrer = new URL(doc.referrer).hostname;
        } catch (e) {
          noop();
        }
      }
      a.landing_page = location.pathname;
      a.langue = doc.documentElement.lang || '';
      a.appareil = window.matchMedia('(max-width: 749px)').matches ? 'mobile' : 'desktop';
      return a;
    };

    const send = () => {
      if (ss.getItem(FLAG)) return;
      ss.setItem(FLAG, '1');
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attrs() }),
        credentials: 'same-origin',
      }).catch(noop);
    };

    // Sans panier créé inutilement : on envoie dès qu'il y a un signal marketing, sinon au premier ajout au panier.
    if (hasCampaign || ext) send();
    doc.addEventListener('cart:update', send, { once: true });
  }

  /* ---------------------------------------------------------------------
     3. Barre d'achat fixe (fiche produit)
     --------------------------------------------------------------------- */
  function initSticky() {
    const bar = doc.querySelector('[data-ar-sticky]');
    if (!bar) return;
    const main = doc.querySelector('#MainContent add-to-cart-component button[name="add"]');
    if (!main) return;
    const btn = bar.querySelector('[data-ar-sticky-btn]');
    const priceEl = bar.querySelector('[data-ar-sticky-price]');

    const syncPrice = () => {
      const p = doc.querySelector('#MainContent product-price .price');
      if (p && priceEl) priceEl.textContent = p.textContent.trim();
    };
    doc.addEventListener('variant:update', () => setTimeout(syncPrice, 60));

    const show = (on) => {
      bar.classList.toggle('is-visible', on);
      bar.setAttribute('aria-hidden', on ? 'false' : 'true');
      doc.body.classList.toggle('ar-has-sticky', on);
      if (on) syncPrice();
    };

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => show(!e.isIntersecting && e.boundingClientRect.top < 0));
        },
        { threshold: 0 }
      ).observe(main);
    }

    if (btn) {
      btn.addEventListener('click', () => {
        const form = main.closest('form');
        const invalid = form && form.querySelector(':invalid');
        if (main.disabled || invalid) {
          const target = invalid || main;
          target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
          if (invalid && invalid.reportValidity) setTimeout(() => invalid.reportValidity(), 350);
          return;
        }
        main.click();
      });
    }
  }

  /* ---------------------------------------------------------------------
     4. Visiteurs en direct (Firebase Realtime Database, REST)
     --------------------------------------------------------------------- */
  function initLive() {
    const els = doc.querySelectorAll('[data-ar-live]');
    if (!els.length || !cfg.live || !window.fetch) return;
    const base = String(cfg.live).replace(/\/+$/, '');
    const WINDOW = 120000;
    const TICK = 40000;
    const min = Math.max(1, parseInt(cfg.liveMin, 10) || 1);

    let sid = ss && ss.getItem('ar_sid');
    if (!sid) {
      sid = Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
      if (ss) ss.setItem('ar_sid', sid);
    }

    const render = (n) => {
      els.forEach((el) => {
        const c = el.querySelector('[data-ar-live-count]');
        if (c) c.textContent = String(n);
        el.hidden = n < min;
      });
    };

    const beat = () =>
      fetch(base + '/presence/' + encodeURIComponent(sid) + '.json', {
        method: 'PUT',
        body: JSON.stringify({ t: { '.sv': 'timestamp' } }),
        keepalive: true,
      }).catch(noop);

    const count = () =>
      fetch(base + '/presence.json?orderBy=%22t%22&startAt=' + (Date.now() - WINDOW))
        .then((r) => (r.ok ? r.json() : null))
        .then((obj) => {
          if (obj === null || typeof obj !== 'object') return;
          render(Math.max(1, Object.keys(obj).length));
        })
        .catch(noop);

    const tick = () => {
      if (doc.hidden) return;
      beat().then(count);
    };

    tick();
    setInterval(tick, TICK);
    doc.addEventListener('visibilitychange', () => {
      if (!doc.hidden) tick();
    });
  }

  /* ---------------------------------------------------------------------
     5. « La lune de votre date »
     --------------------------------------------------------------------- */
  function initMoon(root) {
    let S = {};
    try {
      S = JSON.parse(root.querySelector('[data-ar-moon-strings]').textContent);
    } catch (e) {
      return;
    }
    const form = root.querySelector('[data-ar-moon-form]');
    const input = root.querySelector('[data-ar-moon-date]');
    const out = root.querySelector('[data-ar-moon-result]');
    const lit = root.querySelector('[data-ar-moon-lit]');
    const cta = root.querySelector('[data-ar-moon-cta]');
    if (!form || !input || !out || !lit) return;

    const locale = doc.documentElement.lang || undefined;
    const fmt = (d) => {
      try {
        return new Intl.DateTimeFormat(locale, { dateStyle: 'long', timeZone: 'UTC' }).format(d);
      } catch (e) {
        return d.toISOString().slice(0, 10);
      }
    };

    const show = (psi) => lit.setAttribute('d', moonPath(psi, 100, 100, 92));
    show(180);

    form.addEventListener('submit', (ev) => {
      ev.preventDefault();
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(input.value || '');
      if (!m) {
        out.textContent = S.invalid;
        if (cta) cta.hidden = true;
        return;
      }
      const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], 12, 0, 0));
      if (isNaN(d.getTime())) {
        out.textContent = S.invalid;
        return;
      }
      const psi = moonElongation(d);
      const pct = Math.round(illumination(psi) * 100);
      show(psi);
      out.textContent = S.result
        .replace('{date}', fmt(d))
        .replace('{phase}', S.phases[phaseKey(psi)])
        .replace('{pct}', String(pct));
      if (cta) cta.hidden = false;
    });
  }

  /* ---------------------------------------------------------------------
     6. Pop-up d'inscription (désactivé par défaut)
     --------------------------------------------------------------------- */
  function initPopup() {
    const dlg = doc.querySelector('[data-ar-popup]');
    if (!dlg || typeof dlg.showModal !== 'function' || !cfg.popup) return;
    if (doc.documentElement.classList.contains('shopify-design-mode')) return;
    const KEY = 'ar_popup_seen';
    const DAYS = 7;
    const justPosted = /customer_posted=true/.test(location.search) && dlg.querySelector('.ar-message--ok');

    const seen = () => {
      try {
        const t = parseInt(ls && ls.getItem(KEY), 10);
        return t && Date.now() - t < DAYS * 86400000;
      } catch (e) {
        return false;
      }
    };
    const mark = () => {
      if (ls) ls.setItem(KEY, String(Date.now()));
    };
    const open = () => {
      if (dlg.open || doc.querySelector('dialog[open]')) return;
      dlg.showModal();
      mark();
    };

    dlg.querySelectorAll('[data-ar-popup-close]').forEach((b) => b.addEventListener('click', () => dlg.close()));
    dlg.addEventListener('click', (e) => {
      if (e.target === dlg) dlg.close();
    });

    if (justPosted) {
      open();
      return;
    }
    if (seen()) return;

    const delay = Math.max(5, parseInt(cfg.popupDelay, 10) || 25) * 1000;
    const timer = setTimeout(open, delay);
    if (window.matchMedia('(hover: hover)').matches) {
      doc.addEventListener(
        'mouseout',
        (e) => {
          if (!e.relatedTarget && e.clientY <= 0) {
            clearTimeout(timer);
            open();
          }
        },
        { once: true }
      );
    }
  }

  /* ---------------------------------------------------------------------
     7. Pixels publicitaires (optionnels, chargés seulement après consentement)
     --------------------------------------------------------------------- */
  function initPixels() {
    const px = cfg.pixels || {};
    if (!px.ga4 && !px.meta && !px.tiktok) return;
    let loaded = false;

    const load = () => {
      if (loaded) return;
      loaded = true;
      if (px.ga4) {
        const s = doc.createElement('script');
        s.async = true;
        s.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(px.ga4);
        doc.head.appendChild(s);
        window.dataLayer = window.dataLayer || [];
        window.gtag = function () {
          window.dataLayer.push(arguments);
        };
        window.gtag('js', new Date());
        window.gtag('config', px.ga4, { anonymize_ip: true });
      }
      if (px.meta && !window.fbq) {
        /* eslint-disable */
        !(function (f, b, e, v, n, t, s) {
          if (f.fbq) return;
          n = f.fbq = function () {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
          };
          if (!f._fbq) f._fbq = n;
          n.push = n;
          n.loaded = !0;
          n.version = '2.0';
          n.queue = [];
          t = b.createElement(e);
          t.async = !0;
          t.src = v;
          s = b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t, s);
        })(window, doc, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
        /* eslint-enable */
        window.fbq('init', px.meta);
        window.fbq('track', 'PageView');
      }
      if (px.tiktok && !window.ttq) {
        const w = window;
        const t = 'ttq';
        w.TiktokAnalyticsObject = t;
        const ttq = (w[t] = w[t] || []);
        ttq.methods = ['page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie'];
        ttq.setAndDefer = function (o, m) {
          o[m] = function () {
            o.push([m].concat(Array.prototype.slice.call(arguments, 0)));
          };
        };
        ttq.methods.forEach((m) => ttq.setAndDefer(ttq, m));
        ttq.load = function (id) {
          const s = doc.createElement('script');
          s.async = true;
          s.src = 'https://analytics.tiktok.com/i18n/pixel/events.js?sdkid=' + encodeURIComponent(id) + '&lib=' + t;
          doc.head.appendChild(s);
        };
        ttq.load(px.tiktok);
        ttq.page();
      }
    };

    const allowed = () => {
      const cp = window.Shopify && window.Shopify.customerPrivacy;
      return !!(cp && typeof cp.marketingAllowed === 'function' && cp.marketingAllowed());
    };

    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (err) => {
        if (err) return;
        if (allowed()) load();
        doc.addEventListener('visitorConsentCollected', () => {
          if (allowed()) load();
        });
      });
    }
  }

  /* ---------------------------------------------------------------------
     Démarrage
     --------------------------------------------------------------------- */
  ready(function () {
    doc.querySelectorAll('[data-ar-film]').forEach(initFilm);
    doc.querySelectorAll('[data-ar-moon]').forEach(initMoon);
    initAttribution();
    initSticky();
    initLive();
    initPopup();
    initPixels();
  });
})();
